/**
 * BadgeService
 *
 * Centralized frontend state for calm notification indicators across sidebar,
 * segment navigation tabs, and list/nav cards. Counts are treated as normal
 * navigation metadata; colored indicators are the notification layer.
 */

import { API } from '../core/api.js';
import { isTogetherUnread } from '../components/guideDrillHelper.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import { isGuestName, isNewSinceViewed } from '../core/utils.js';
import {
  CONTENT_VIEWED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED,
  CONTENT_ACTIVATED
} from '../core/contentSync.js';

const INDICATOR_PRIORITY = {
  red: 3,
  orange: 2,
  blue: 1
};

/**
 * DISCOVERY URGENT BADGE GATE — temporarily disabled 2026-07-27: urgent discovery
 * tasks now surface via the dashboard's Recommended Activities section instead.
 * Flip to `true` to restore the discovery sidebar dot and the /discovery paths-tab
 * dot. Scope: only the rolled-up sidebar/tab indicator computed in
 * loadDiscoveryStatus() below — per-path card pills ("waiting on you" /
 * HIGH PRIORITY on individual path cards) are untouched.
 *
 * Hazard note: Sidebar.setNavigationState destructures a `tag` param from
 * badge:navigation-update events but never renders it — do not wire it into
 * the DOM without re-checking this gate, or the suppressed urgent text
 * silently returns to the sidebar.
 */
const DISCOVERY_URGENT_BADGES_ENABLED = false;

const ATTENTION_DAYS = 7;
const OVERDUE_DAYS = 30;

/**
 * Trailing window for coalescing count refreshes. Sized for `_runBulk`, which
 * fires one request per selected practice and emits per resolution — a
 * microtask flush would issue one refetch per item.
 */
const COUNT_REFRESH_DEBOUNCE_MS = 250;

/**
 * @param {string|null} color
 * @returns {{color: string, type: string}|null}
 */
function createIndicator(color) {
  if (!color) return null;
  return {
    color,
    type: color === 'blue' ? 'new' : 'attention'
  };
}

/** Viewed-state indicator factories shared by initial loads and event revives. */
const SECTION_REVIVE_INDICATOR = {
  insights: () => ({ ...createIndicator('blue'), kind: 'pill', text: 'new' }),
  notes: () => ({ ...createIndicator('blue') })
};

const VIEWED_BADGE_SECTION = {
  guide: 'insights',
  sharedGuide: 'insights',
  note: 'notes'
};

/**
 * Namespace prefix for the couple's half of a guide's unread state (D3). The
 * two halves of one `/library` guide card live under two keys in the SAME
 * `cards.insights` bucket — `<definitionId>` for the viewer's own reading and
 * `together:<definitionId>` for the couple's — so either can be lit, and
 * cleared, without touching the other.
 */
const TOGETHER_CARD_PREFIX = 'together:';

/**
 * The `cards.insights` key an unread event names, from its `itemType`.
 * @param {string} itemType - `guide` (the viewer's own reading) or `sharedGuide`
 * @param {string} itemId - Guide definition id
 * @returns {string|null} Card key, or null for an itemType that doesn't badge here
 */
function insightsCardKey(itemType, itemId) {
  if (itemType === 'guide') return itemId;
  if (itemType === 'sharedGuide') return `${TOGETHER_CARD_PREFIX}${itemId}`;
  return null;
}

/**
 * Return the highest-priority indicator.
 * @param  {...({color: string}|null|undefined)} indicators
 * @returns {{color: string, type: string}|null}
 */
function highestPriorityIndicator(...indicators) {
  const valid = indicators.filter(Boolean);
  if (!valid.length) return null;
  return valid.sort((a, b) =>
    (INDICATOR_PRIORITY[b.color] || 0) - (INDICATOR_PRIORITY[a.color] || 0)
  )[0];
}

export class BadgeService {
  /**
   * @param {Object} eventBus - Event bus for component communication
   * @param {string} userId - Current user ID
   * @param {string} coupleId - Current user's couple ID
   */
  constructor(eventBus, userId, coupleId) {
    this.eventBus = eventBus;
    this.userId = userId;
    this.coupleId = coupleId;
    this.discoveryAPI = new DiscoveryAPI();

    this.state = this.createEmptyState();
    this.previewState = this.createEmptyState();
    this._badgeStatusLoadInFlight = false;
    this._pendingContentEvents = [];
    this._replayingContentEvents = false;

    // Targeted count reconciliation (see scheduleCountRefresh).
    this._pendingCountRefreshes = new Set();
    this._countRefreshTimer = null;
    this._countRefreshChains = { practices: Promise.resolve(), notes: Promise.resolve() };
    this._flushPromise = null;

    // Mode-lifecycle badge reconciliation. Badges are suppressed while
    // orientation is live and repopulated once it ends; we reload only when the
    // live/not-live edge is actually crossed, and debounce so the dismissal
    // double-fire (`orientation:dismissed` + `mode:cleared`) coalesces into one.
    this._modeReconcileTimer = null;
    this._lastOrientationLive = this._orientationLive();
  }

  /**
   * @returns {Object} Empty badge state.
   */
  createEmptyState() {
    return {
      sidebar: {
        practices: { count: null, indicator: null },
        discovery: { count: null, indicator: null },
        insights: { count: null, indicator: null },
        notes: { count: null, indicator: null }
      },
      tabs: {
        discovery: {},
        insights: {}
      },
      cards: {
        practices: {},
        discovery: {},
        insights: {},
        notes: {}
      },
      legacy: {
        discovery: {
          show: false,
          text: null,
          type: null,
          statusText: null,
          inProgressCount: 0,
          partnerWaitingName: null
        },
        insights: {
          show: false,
          text: null,
          type: null,
          statusText: null,
          unviewedIds: new Set()
        }
      }
    };
  }

  /**
   * Initialize the service.
   */
  async initialize() {
    this.setupEventListeners();
    await this.loadBadgeStatus();
    this.installDevTools();
  }

  /**
   * Load all current badge/count status data.
   *
   * Sidebar notification dots are suppressed during orientation: a brand-new
   * user has nothing meaningful to be notified about, and surfacing dots
   * competes with the orientation flow. Mirrors FeatureToggleService's
   * onboardingStep-driven gating; the `orientation:completed` listener
   * repopulates state once the user finishes.
   */
  async loadBadgeStatus() {
    // A full load refetches everything a targeted refresh would have; drop any
    // armed one so it can't land mid-load and write onto freshly built state.
    clearTimeout(this._countRefreshTimer);
    this._countRefreshTimer = null;
    this._pendingCountRefreshes.clear();

    this._badgeStatusLoadInFlight = true;
    try {
      if (this._orientationLive()) {
        this.state = this.createEmptyState();
      } else {
        await Promise.all([
          this.loadPracticesStatus(),
          this.loadDiscoveryStatus(),
          this.loadInsightsStatus(),
          this.loadNotesStatus()
        ]);
      }
    } finally {
      this._badgeStatusLoadInFlight = false;
      const pending = this._pendingContentEvents.splice(0);
      this._replayingContentEvents = true;
      try {
        for (const { event, data } of pending) {
          this._applyContentEvent(event, data);
        }
      } finally {
        this._replayingContentEvents = false;
      }
      this.emitStateChanged();
    }
  }

  /**
   * Whether the orientation UI is live on screen right now. Badges are
   * suppressed while it is: a brand-new user has nothing meaningful to be
   * notified about, and dots would compete with the orientation flow. This is a
   * *live* predicate (mirrors FeatureToggleService) rather than the historical
   * `onboardingStep`, so a user who exits orientation early gets working badges
   * in-session instead of empty ones until reload. Null-safe: BadgeService
   * initializes before ModeOrchestrator, so `_pendingOrientation` is the
   * fallback signal before a mode exists.
   *
   * @returns {boolean}
   */
  _orientationLive() {
    return window.modeOrchestrator?.currentMode?.name === 'ORIENTATION'
      || !!window.app?._pendingOrientation;
  }

  /**
   * True while the visitor is a guest. Read live from `window.app` on every
   * call, never snapshotted in the constructor: `handleGuestConversion`
   * replaces `app.currentUser` in place, and the practices/guides loaders are
   * `requireAuth` routes that 401 for a guest.
   *
   * @returns {boolean}
   */
  _isGuest() {
    return !!window.app?.currentUser?.isGuest;
  }

  /**
   * Queue an authoritative count refetch for one sidebar section, coalescing
   * bursts into a single trailing fetch.
   *
   * Counts are refetched rather than incremented locally: the content events
   * carry no direction-aware payload, and bulk actions, restores, idempotent
   * retries, and sent-vs-received notes would all drift under arithmetic.
   *
   * The guarantee is one fetch per *coalesced burst*, not one per user action —
   * a bulk mutation whose responses straddle the window produces a second
   * trailing reconciliation, which is correct (it sees the final state).
   *
   * @param {'practices'|'notes'} section
   */
  scheduleCountRefresh(section) {
    // Replay runs after a full load already refetched every section.
    if (this._replayingContentEvents) return;
    // Orientation deliberately suppresses badges; the mode-lifecycle reconcile
    // repopulates everything once orientation ends.
    if (this._orientationLive()) return;

    this._pendingCountRefreshes.add(section);
    clearTimeout(this._countRefreshTimer);
    this._countRefreshTimer = setTimeout(() => this._flushCountRefreshes(), COUNT_REFRESH_DEBOUNCE_MS);
  }

  /**
   * Refetch every section queued since the last flush, then publish once.
   * @returns {Promise<void>|undefined} Retained as `_flushPromise` for tests.
   */
  _flushCountRefreshes() {
    this._countRefreshTimer = null;
    // A full load started after this flush was armed; it supersedes.
    if (this._badgeStatusLoadInFlight) {
      this._pendingCountRefreshes.clear();
      return undefined;
    }

    const sections = [...this._pendingCountRefreshes];
    this._pendingCountRefreshes.clear();
    if (!sections.length) return undefined;

    this._flushPromise = Promise.all(sections.map((section) => {
      const load = section === 'practices'
        ? () => this.loadPracticesStatus()
        : () => this.loadNotesStatus();
      // Chain per section so two overlapping flushes cannot interleave their
      // writes to `this.state` — a stale response can never land after a fresh
      // one. The loaders swallow their own errors, so the chain never rejects.
      this._countRefreshChains[section] = this._countRefreshChains[section].then(load);
      return this._countRefreshChains[section];
    }))
      .then(() => this.emitStateChanged())
      // `eventBus.emit` is an unguarded forEach and `emitStateChanged` fans out
      // to every section; a listener bug would otherwise surface as an
      // unhandled rejection from a detached timer with no stack context.
      .catch(error => console.error('[BadgeService] Count refresh failed:', error));

    return this._flushPromise;
  }

  /**
   * Cancel pending count work. Called on logout so a flush can't fire against a
   * dead session and 401 on the login overlay.
   */
  destroy() {
    clearTimeout(this._countRefreshTimer);
    this._countRefreshTimer = null;
    this._pendingCountRefreshes.clear();
    clearTimeout(this._modeReconcileTimer);
    this._modeReconcileTimer = null;
  }

  /**
   * Load the Practices count.
   *
   * Count only — experiments carry no attention dot. An experiment runs for an
   * observation window, not on a chore cadence, so a passed window is an
   * invitation to report back; the dashboard's "Overdue" row (and the row's own
   * chip on /practices) is where that surfaces. The sidebar count is plain
   * inventory: every active experiment, whatever its window.
   */
  async loadPracticesStatus() {
    if (this._isGuest()) return;
    try {
      const practices = await API.getPractices({ status: 'active', limit: 200 });
      // getPractices resolves null on network failure rather than throwing, so
      // the catch below never fires — coercing that to [] would publish a false
      // zero. Keep the last known-good count.
      if (!Array.isArray(practices)) return;

      this.state.sidebar.practices = { count: practices.length, indicator: null };
    } catch (error) {
      console.error('[BadgeService] Error loading practice status:', error);
    }
  }

  /**
   * Load Discovery status from existing path progress.
   */
  async loadDiscoveryStatus() {
    try {
      const result = this.coupleId
        ? await this.discoveryAPI.getPathsWithCoupleProgress(this.coupleId)
        : await this.discoveryAPI.getPathsWithUserProgress();

      const paths = result?.data?.paths || [];
      const cards = {};
      let userInProgressCount = 0;
      let partnerWaitingName = null;

      const isOnboardingComplete = window.app?.currentUser?.onboardingStep === 'complete';

      for (const path of paths) {
        const userProgress = path.userProgress
          || path.partnerProgress?.find(p => p.userId === this.userId)
          || path.partnerProgress?.[0];
        const partnerProgress = path.partnerProgress?.find(p => p.userId !== this.userId);
        const userIncomplete = !userProgress?.isComplete;

        if (path.partnerProgress?.length >= 2 && userIncomplete) {
          userInProgressCount++;
        }

        // A finished partner is "waiting" only when their completion blocks a
        // shared artifact (path.partnerCompletionBlocks, backend-computed from
        // SHARED_GUIDES_ENABLED + the path's membership in the shared-guide
        // registry). Individual-only paths never set it — a finished
        // partner there is informational (green completed pill), never urgent.
        // Boolean() so an absent field fails safe (not waiting). The red pill
        // also fires when the shared guide's disclosure-set confirm is owed by
        // the viewer (path.sharingConfirmOwedByYou, viewer-master-gated,
        // absent when not applicable) — a completion-symmetric path can still
        // be waiting on the viewer's confirm.
        const partnerBlocking = Boolean(path.partnerCompletionBlocks) &&
          partnerProgress?.isComplete && userIncomplete;
        const confirmOwedByYou = Boolean(path.sharingConfirmOwedByYou);
        const urgentForYou = partnerBlocking || confirmOwedByYou;

        if (urgentForYou && !partnerWaitingName) {
          partnerWaitingName = (partnerProgress?.firstName && !isGuestName(partnerProgress.firstName))
            ? partnerProgress.firstName
            : 'Your partner';
        }

        if (urgentForYou) {
          cards[path.id] = {
            indicator: { ...createIndicator('red'), kind: 'pill', text: 'waiting on you' }
          };
        } else if (path.id === 'foundation' && userIncomplete && isOnboardingComplete) {
          cards[path.id] = {
            indicator: { ...createIndicator('red'), kind: 'pill', text: 'high priority' }
          };
        }
      }

      // DISCOVERY URGENT BADGE GATE — see const above; sidebar dot + paths-tab dot only.
      const sidebarIndicator = DISCOVERY_URGENT_BADGES_ENABLED
        ? highestPriorityIndicator(...Object.values(cards).map(card => card.indicator))
        : null;
      this.state.cards.discovery = cards;
      this.state.tabs.discovery = {
        paths: { indicator: sidebarIndicator }
      };
      this.state.sidebar.discovery = { count: null, indicator: sidebarIndicator };

      this.state.legacy.discovery = {
        show: !!sidebarIndicator,
        text: partnerWaitingName ? 'WAITING ON YOU' : (cards.foundation ? 'RECOMMENDED' : null),
        type: partnerWaitingName ? 'red' : (cards.foundation ? 'red' : null),
        statusText: partnerWaitingName
          ? 'Your shared guide is waiting on you'
          : null,
        inProgressCount: userInProgressCount,
        partnerWaitingName
      };
    } catch (error) {
      console.error('[BadgeService] Error loading discovery status:', error);
    }
  }

  /**
   * Load Insights status (unviewed guides → card pills, tab dots, sidebar
   * indicator). Patterns intentionally do not badge.
   *
   * A `/library` guide card carries TWO unread states — the viewer's own
   * reading and the couple's — under two keys in this one bucket (D3), so the
   * card word and the Guides-tab dot light when EITHER is set and each half
   * clears independently.
   *
   * The shared-guides manifest is fetched ONLY for a linked user (the
   * `loadNotesStatus` guard): `GET /couples/shared-guides` seals coordination
   * rows and can start generation, so it must never run from an unlinked
   * badge poll. It never throws, so a failure there degrades to "no Together
   * unread" and leaves the own-guide half untouched.
   */
  async loadInsightsStatus() {
    if (this._isGuest()) return;
    try {
      const [guidesResult, sharedResult] = await Promise.all([
        API.getAvailableGuides(),
        this.coupleId ? API.getSharedGuides() : Promise.resolve([])
      ]);
      const guides = Array.isArray(guidesResult) ? guidesResult : [];
      const sharedGuides = Array.isArray(sharedResult) ? sharedResult : [];
      const cards = {};
      const tabs = {};

      for (const guide of guides) {
        if (guide.status === 'READY' && isNewSinceViewed(guide.generatedAt || guide.createdAt, guide.viewedAt)) {
          cards[guide.id] = {
            indicator: SECTION_REVIVE_INDICATOR.insights()
          };
        }
      }

      for (const shared of sharedGuides) {
        if (shared?.guideDefinitionId && isTogetherUnread(shared)) {
          cards[`${TOGETHER_CARD_PREFIX}${shared.guideDefinitionId}`] = {
            indicator: SECTION_REVIVE_INDICATOR.insights()
          };
        }
      }

      if (Object.keys(cards).length > 0) {
        tabs.guides = { indicator: createIndicator('blue') };
      }

      const sidebarIndicator = highestPriorityIndicator(...Object.values(cards).map(card => card.indicator));
      this.state.cards.insights = cards;
      this.state.tabs.insights = tabs;
      this.state.sidebar.insights = { count: null, indicator: sidebarIndicator };
      this.state.legacy.insights = {
        show: !!sidebarIndicator,
        text: null,
        type: sidebarIndicator?.color || null,
        statusText: null,
        unviewedIds: new Set(Object.keys(cards))
      };
    } catch (error) {
      console.error('[BadgeService] Error loading insights status:', error);
    }
  }

  /**
   * Load Notes status: the sidebar count is active (non-archived) received
   * notes only — sent notes and archived notes don't count. The blue "new"
   * dot fires when any of those active received notes are unread
   * (viewedAt null). Couple-gated: the sidebar item is hidden without a
   * linked partner, so skip the fetch entirely when unlinked.
   */
  async loadNotesStatus() {
    if (!this.coupleId) {
      this.state.cards.notes = {};
      this.state.sidebar.notes = { count: null, indicator: null };
      return;
    }
    try {
      const result = await this.discoveryAPI.getMemos();
      const notes = Array.isArray(result?.data) ? result.data : [];
      const activeReceived = notes.filter(note => !note?.archivedAt);
      const cards = {};

      for (const note of activeReceived) {
        if (!note?.viewedAt && note?.id) {
          cards[note.id] = { indicator: SECTION_REVIVE_INDICATOR.notes() };
        }
      }

      this.state.cards.notes = cards;
      this.state.sidebar.notes = {
        count: activeReceived.length,
        indicator: highestPriorityIndicator(...Object.values(cards).map(card => card.indicator))
      };
    } catch (error) {
      console.error('[BadgeService] Error loading notes status:', error);
    }
  }

  /**
   * @param {Object} options
   * @param {string|Date|null} [options.viewedAt]
   * @param {string|Date|null} [options.lastActivityAt]
   * @param {string|Date|null} [options.createdAt]
   * @param {string|Date|null} [options.updatedAt]
   * @param {boolean} options.hasActivity
   * @returns {{color: string, type: string}|null}
   */
  getActivityIndicator({ viewedAt, lastActivityAt, createdAt, updatedAt, hasActivity }) {
    if (!viewedAt && !hasActivity) return createIndicator('amber');

    const recencyDate = lastActivityAt || viewedAt || updatedAt || createdAt;
    const ageDays = this.getDateAgeInDays(recencyDate);
    if (ageDays >= OVERDUE_DAYS) return createIndicator('red');
    if (ageDays > ATTENTION_DAYS) return createIndicator('amber');
    return null;
  }

  /**
   * @param {string|Date|null|undefined} date
   * @returns {number}
   */
  getDateAgeInDays(date) {
    if (!date) return Number.POSITIVE_INFINITY;
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return Number.POSITIVE_INFINITY;
    const diffMs = Date.now() - parsed.getTime();
    if (diffMs <= 0) return 0;
    return Math.floor(diffMs / 86400000);
  }

  /**
   * Emit state update events for every UI layer.
   */
  emitStateChanged() {
    const state = this.getMergedState();

    for (const [badgeId, value] of Object.entries(state.sidebar)) {
      const sectionCards = Object.values(state.cards[badgeId] || {});
      const topCard = sectionCards
        .filter(c => c.indicator?.text)
        .sort((a, b) => (INDICATOR_PRIORITY[b.indicator.color] || 0) - (INDICATOR_PRIORITY[a.indicator.color] || 0))[0];
      const tag = topCard
        ? { text: topCard.indicator.text.toUpperCase(), color: topCard.indicator.color }
        : null;
      this.eventBus.emit('badge:navigation-update', {
        badgeId,
        count: value.count,
        indicator: value.indicator,
        tag
      });
    }

    this.eventBus.emit('badge:state-changed', state);
  }

  /**
   * Setup event listeners for state changes.
   */
  setupEventListeners() {
    this.eventBus.on(CONTENT_VIEWED, (data) => {
      this._queueOrApplyContentEvent(CONTENT_VIEWED, data);
    });
    this.eventBus.on(CONTENT_ARCHIVED, (data) => {
      this._queueOrApplyContentEvent(CONTENT_ARCHIVED, data);
    });
    this.eventBus.on(CONTENT_DELETED, (data) => {
      this._queueOrApplyContentEvent(CONTENT_DELETED, data);
    });
    this.eventBus.on(CONTENT_ACTIVATED, (data) => {
      this._queueOrApplyContentEvent(CONTENT_ACTIVATED, data);
    });

    this.eventBus.on('orientation:completed', () => {
      this.loadBadgeStatus();
    });

    // Reconcile badges across the orientation boundary in-session. Exiting
    // orientation emits `mode:cleared` (+ `orientation:dismissed` from the ✕),
    // and resuming emits `mode:changed` into ORIENTATION; none of these touch
    // `onboardingStep`, so badges must reload when orientation liveness flips.
    const reconcile = () => this._reconcileBadgesForMode();
    this.eventBus.on('mode:changed', reconcile);
    this.eventBus.on('mode:restored', reconcile);
    this.eventBus.on('mode:cleared', reconcile);
    this.eventBus.on('orientation:dismissed', reconcile);

    // Guest conversion (`handleGuestConversion` emits `user:updated`) re-arms
    // the requireAuth loaders that `_isGuest()` skipped at boot. Fire-and-
    // forget: `loadBadgeStatus` swallows its own errors and self-gates on
    // `_orientationLive()`, so a mid-orientation conversion writes empty
    // state and `orientation:completed` repopulates it.
    this.eventBus.on('user:updated', () => {
      if (this._isGuest()) return;
      this.loadBadgeStatus();
    });
  }

  /**
   * Reload badges when the orientation-live edge is crossed. Reloading only on
   * an actual liveness change (not on every mode switch) avoids needless
   * refetches, and the debounce coalesces the dismissal double-fire
   * (`orientation:dismissed` immediately followed by `mode:cleared`) into a
   * single reload. `loadBadgeStatus` self-gates on `_orientationLive()`, so it
   * clears on entry to orientation and repopulates on exit.
   * @private
   */
  _reconcileBadgesForMode() {
    const live = this._orientationLive();
    if (live === this._lastOrientationLive) return;
    this._lastOrientationLive = live;

    clearTimeout(this._modeReconcileTimer);
    this._modeReconcileTimer = setTimeout(() => {
      this._modeReconcileTimer = null;
      this.loadBadgeStatus();
    }, COUNT_REFRESH_DEBOUNCE_MS);
  }

  /**
   * Queue content mutations while a full badge load owns the state buckets;
   * otherwise apply them immediately.
   * @param {string} event
   * @param {Object} data
   */
  _queueOrApplyContentEvent(event, data) {
    if (this._badgeStatusLoadInFlight) {
      this._pendingContentEvents.push({ event, data });
      return;
    }
    this._applyContentEvent(event, data);
  }

  /**
   * @param {string} event
   * @param {Object} data
   */
  _applyContentEvent(event, data) {
    if (event === CONTENT_VIEWED) {
      this._onContentViewed(data);
    } else if (event === CONTENT_ARCHIVED) {
      this._onContentArchived(data);
    } else if (event === CONTENT_DELETED) {
      this._onContentDeleted(data);
    } else if (event === CONTENT_ACTIVATED) {
      this._onContentActivated(data);
    }
  }

  /**
   * A draft practice became active — the Practices count is otherwise blind to
   * acceptance, since nothing else in the accept flow touches badge state.
   * @param {{itemType?: string, itemId?: string}} data
   */
  _onContentActivated(data = {}) {
    try {
      const { itemType } = data || {};
      if (itemType === 'practice') this.scheduleCountRefresh('practices');
    } catch (error) {
      console.error('[BadgeService] Error applying content:activated:', error);
    }
  }

  /**
   * Reflect a confirmed viewed-state mutation in badge notification state.
   * @param {{itemType?: string, itemId?: string, viewed?: boolean}} data
   */
  _onContentViewed(data = {}) {
    try {
      const { itemType, itemId, viewed } = data || {};
      const section = VIEWED_BADGE_SECTION[itemType];
      if (!section || !itemId || (viewed !== true && viewed !== false)) return;
      // A guide's two halves are two keys in one bucket (D3) — clear or revive
      // exactly the one this event names.
      const key = section === 'insights' ? insightsCardKey(itemType, itemId) : itemId;
      if (!key) return;

      if (viewed === true) {
        this.markCardViewed(section, key);
      } else {
        this.reviveCard(section, key);
      }
    } catch (error) {
      console.error('[BadgeService] Error applying content:viewed:', error);
    }
  }

  /**
   * Clear active unread cards when content leaves the active surface, and
   * reconcile the count for sections whose membership changed.
   *
   * The count refresh runs in BOTH directions (a restore re-adds to the active
   * count), so it precedes the `archived !== true` guard. The card clears stay
   * archive-only: reviving a restored note's dot is the refetch's job, not
   * this handler's. Archived READY guides are included in the card clears
   * because guide archive does not change their generation status.
   * @param {{itemType?: string, itemId?: string, archived?: boolean}} data
   */
  _onContentArchived(data = {}) {
    try {
      const { itemType, itemId, archived } = data || {};
      if (!itemId) return;
      if (itemType === 'practice') this.scheduleCountRefresh('practices');
      if (itemType === 'note') this.scheduleCountRefresh('notes');

      if (archived !== true) return;
      if (itemType === 'note') this.markCardViewed('notes', itemId);
      // Archiving a half puts that half away, so its unread state goes with
      // it — and only its own (D3/D8): archiving your reading never clears the
      // couple's "Together added".
      const insightsKey = insightsCardKey(itemType, itemId);
      if (insightsKey) this.markCardViewed('insights', insightsKey);
    } catch (error) {
      console.error('[BadgeService] Error applying content:archived:', error);
    }
  }

  /**
   * Remove deleted notes and guides from their unread buckets, and reconcile
   * the count for sections whose membership changed. A deleted guide clears
   * exactly like an archived one (`_onContentArchived`) — its "new" pill has
   * nothing left to point at, and leaving the card would strand the Library
   * sidebar dot and guides-tab dot lit for a row that no longer exists. Only
   * the viewer's OWN key clears: the couple's shared artifact has no delete
   * path at all, so a `guide` delete must not reach across to it.
   * @param {{itemType?: string, itemId?: string}} data
   */
  _onContentDeleted(data = {}) {
    try {
      const { itemType, itemId } = data || {};
      if (!itemId) return;
      if (itemType === 'practice') this.scheduleCountRefresh('practices');
      if (itemType === 'guide') this.markCardViewed('insights', itemId);
      if (itemType !== 'note') return;
      this.scheduleCountRefresh('notes');
      this.markCardViewed('notes', itemId);
    } catch (error) {
      console.error('[BadgeService] Error applying content:deleted:', error);
    }
  }

  /**
   * Clear a card after a confirmed `content:viewed` mutation. Persistence is
   * owned by the emitting surface; this method updates notification state only.
   * @param {string} section
   * @param {string} id
   */
  markCardViewed(section, id) {
    if (!section || !id) return;
    delete this.state.cards?.[section]?.[id];
    delete this.previewState.cards?.[section]?.[id];
    if (section === 'insights') this._recomputeInsightsGuidesTab();
    this.recomputeSidebarFromCards(section);
    if (!this._replayingContentEvents) this.emitStateChanged();
  }

  /**
   * Restore a viewed-state notification card after a confirmed unview event.
   * @param {'insights'|'notes'} section
   * @param {string} id
   */
  reviveCard(section, id) {
    if (this.state.cards[section]?.[id]) return;
    const createReviveIndicator = SECTION_REVIVE_INDICATOR[section];
    if (!createReviveIndicator || !id) return;
    this.state.cards[section][id] = { indicator: createReviveIndicator() };
    if (section === 'insights') this._recomputeInsightsGuidesTab();
    this.recomputeSidebarFromCards(section);
    if (!this._replayingContentEvents) this.emitStateChanged();
  }

  /**
   * Recompute the guides-tab dot from the live insights card bucket. Both key
   * shapes live in that one bucket (`<definitionId>` and
   * `together:<definitionId>`), so this ORs them by construction: the dot
   * shows while EITHER half of any guide card is unread.
   */
  _recomputeInsightsGuidesTab() {
    if (Object.keys(this.state.cards.insights || {}).length > 0) {
      this.state.tabs.insights.guides = { indicator: createIndicator('blue') };
    } else {
      delete this.state.tabs.insights.guides;
    }
  }

  /**
   * @param {string} section
   */
  recomputeSidebarFromCards(section) {
    if (!this.state.sidebar[section]) return;
    const cardIndicators = Object.values(this.state.cards[section] || {}).map(card => card.indicator);
    const previewIndicators = Object.values(this.previewState.cards[section] || {}).map(card => card.indicator);
    const currentCount = this.state.sidebar[section].count;
    this.state.sidebar[section] = {
      count: currentCount,
      indicator: highestPriorityIndicator(...cardIndicators, ...previewIndicators)
    };
  }

  /**
   * Check if an insight is unviewed (insights bucket: patterns + guides).
   * @param {string} insightId
   * @returns {boolean}
   */
  isInsightUnviewed(insightId) {
    return !!this.getMergedState().cards.insights?.[insightId];
  }

  /**
   * @returns {Object}
   */
  getState() {
    return this.getMergedState();
  }

  /**
   * @returns {Object}
   */
  getMergedState() {
    const base = this.cloneState(this.state);
    const preview = this.cloneState(this.previewState);

    for (const [section, value] of Object.entries(preview.sidebar)) {
      if (value.count !== null || value.indicator) {
        base.sidebar[section] = {
          count: value.count ?? base.sidebar[section]?.count ?? null,
          indicator: highestPriorityIndicator(value.indicator, base.sidebar[section]?.indicator)
        };
      }
    }

    for (const [page, tabs] of Object.entries(preview.tabs)) {
      base.tabs[page] = { ...(base.tabs[page] || {}), ...tabs };
    }

    for (const [section, cards] of Object.entries(preview.cards)) {
      base.cards[section] = { ...(base.cards[section] || {}), ...cards };
    }

    return base;
  }

  /**
   * @param {Object} state
   * @returns {Object}
   */
  cloneState(state) {
    return {
      sidebar: JSON.parse(JSON.stringify(state.sidebar)),
      tabs: JSON.parse(JSON.stringify(state.tabs)),
      cards: JSON.parse(JSON.stringify(state.cards)),
      legacy: {
        discovery: { ...state.legacy.discovery },
        insights: {
          ...state.legacy.insights,
          unviewedIds: new Set(state.legacy.insights.unviewedIds || [])
        }
      }
    };
  }

  /**
   * Get discovery status for existing dashboard card integration.
   * @returns {Object}
   */
  getDiscoveryStatus() {
    const d = this.getMergedState().legacy.discovery;
    const indicator = this.getSidebarIndicator('discovery');
    const tag = d.text ? { text: d.text, color: d.type || 'red' } : null;
    return {
      indicator,
      tag,
      statusText: d.statusText,
      inProgressCount: d.inProgressCount,
      partnerWaitingName: d.partnerWaitingName
    };
  }

  /**
   * @param {string} section
   * @returns {{color: string, type: string}|null}
   */
  getSidebarIndicator(section) {
    return this.getMergedState().sidebar?.[section]?.indicator || null;
  }

  /**
   * Roll up every sidebar section's indicator into a single highest-priority
   * indicator, for surfaces (e.g. the mobile hamburger menu button) that sit
   * above the whole sidebar and can only show one dot.
   * @returns {{color: string, type: string}|null}
   */
  getAggregateIndicator() {
    const sidebar = this.getMergedState().sidebar;
    return highestPriorityIndicator(...Object.values(sidebar).map(value => value.indicator));
  }

  /**
   * @param {string} page
   * @param {string} tab
   * @returns {{color: string, type: string}|null}
   */
  getTabIndicator(page, tab) {
    return this.getMergedState().tabs?.[page]?.[tab]?.indicator || null;
  }

  /**
   * @param {string} section
   * @param {string} id
   * @returns {{indicator: Object}|null}
   */
  getCardState(section, id) {
    return this.getMergedState().cards?.[section]?.[id] || null;
  }

  /**
   * @param {string} section
   * @param {string} id
   * @returns {string}
   */
  renderCardIndicator(section, id) {
    const indicator = this.getCardState(section, id)?.indicator;
    if (!indicator) return '';
    const label = indicator.text || 'new';
    return `<span class="notification-label notification-label--${indicator.color}">${label}</span>`;
  }

  /**
   * Force refresh badge status.
   */
  async refresh() {
    await this.loadBadgeStatus();
  }

  /**
   * Set preview notification state from the Chrome console.
   * @param {Object} state
   */
  preview(state = {}) {
    this.previewState = this.createEmptyState();
    this.applyPreviewState(state);
    this.emitStateChanged();
  }

  /**
   * Merge a preview object into preview state.
   * @param {Object} state
   */
  applyPreviewState(state) {
    const normalizeIndicator = (indicator) => {
      if (!indicator) return null;
      if (typeof indicator === 'string') return createIndicator(indicator);
      return {
        type: indicator.type || (indicator.color === 'blue' ? 'new' : 'attention'),
        kind: indicator.kind,
        text: indicator.text,
        color: indicator.color
      };
    };

    for (const [section, value] of Object.entries(state.sidebar || {})) {
      this.previewState.sidebar[section] = {
        count: value.count ?? null,
        indicator: normalizeIndicator(value.indicator || value.color)
      };
    }

    for (const [page, tabs] of Object.entries(state.tabs || {})) {
      this.previewState.tabs[page] = {};
      for (const [tab, value] of Object.entries(tabs)) {
        this.previewState.tabs[page][tab] = {
          indicator: normalizeIndicator(value.indicator || value.color)
        };
      }
    }

    for (const [section, cards] of Object.entries(state.cards || {})) {
      this.previewState.cards[section] = {};
      for (const [id, value] of Object.entries(cards)) {
        const indicator = normalizeIndicator(value.indicator || value.color);
        if (indicator) {
          this.previewState.cards[section][id] = {
            indicator: {
              ...indicator,
              kind: indicator.kind || 'pill'
            }
          };
        }
      }

      const sectionIndicator = highestPriorityIndicator(
        ...Object.values(this.previewState.cards[section]).map(card => card.indicator)
      );
      if (sectionIndicator && !this.previewState.sidebar[section]?.indicator) {
        this.previewState.sidebar[section] = {
          count: this.previewState.sidebar[section]?.count ?? null,
          indicator: sectionIndicator
        };
      }
    }
  }

  /**
   * Clear all preview notification state.
   */
  clearPreview() {
    this.previewState = this.createEmptyState();
    this.emitStateChanged();
  }

  /**
   * Install concise Chrome console helpers.
   */
  installDevTools() {
    window.badgePreview = (state) => this.preview(state);
    window.clearBadgePreview = () => this.clearPreview();
    window.badgeDemo = () => this.preview({
      sidebar: {
        discovery: { indicator: 'red' },
        insights: { indicator: 'blue' }
      },
      tabs: {
        discovery: {
          paths: { indicator: 'red' }
        },
        insights: {
          patterns: { indicator: 'blue' },
          guides: { indicator: 'blue' }
        }
      }
    });
  }
}
